/**
 * API Service
 * Centralized HTTP client with authentication and error handling
 */

import axios from 'axios';
import { auth } from '@/config/firebase';
import toast from 'react-hot-toast';
import logger from '@/utils/logger';

/**
 * Base API URL from environment with safe defaults
 * - Prefer explicit env override
 * - Fallback to same-origin /api so production builds don't point to localhost
 * - Last resort: localhost for local dev
 */
const deriveBaseUrl = () => {
  // Explicit override wins
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // In Vite dev (5173), target backend port 3000 directly (no proxy configured)
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const origin = window.location?.origin || '';
    const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');
    if (isLocalhost) {
      return 'http://localhost:3000/api';
    }
  }

  // Same-origin /api (for production builds)
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }

  // Last resort: local dev default
  return 'http://localhost:3000/api';
};

const BASE_URL = deriveBaseUrl();

/**
 * Create axios instance with base configuration
 */
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to add authentication token
 */
api.interceptors.request.use(
  async (config) => {
    try {
      // Get current user's ID token
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken(false);
        config.headers.Authorization = `Bearer ${idToken}`;
        
        // Add user ID for debugging (optional)
        config.headers['X-User-ID'] = user.uid;
      }
      
      // Log request in development
      if (import.meta.env.DEV) {
        logger.debug(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
          data: config.data,
          params: config.params
        });
      }
      
      return config;
    } catch (error) {
      logger.error('Error adding auth token to request:', error);
      return config;
    }
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling and logging
 */
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      logger.debug(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log error in development
    if (import.meta.env.DEV) {
      logger.error(`❌ API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }

    // List of endpoints where we suppress error toasts (handled gracefully by components)
    // These are AI/ML features that can fail silently without disrupting user flow
    const silentEndpoints = [
      '/insights/suggest-items',
      '/insights/generate-notes', 
      '/insights/enhance-description',
      '/search/suggest-category'
    ];
    
    const requestUrl = originalRequest?.url || '';
    const isSilentEndpoint = silentEndpoints.some(endpoint => requestUrl.includes(endpoint));

    // Handle authentication errors
    if (error.response?.status === 401) {
      const errorData = error.response.data;
      
      // Check if token is expired or invalid
      if (errorData?.code?.includes('token-expired') || errorData?.code?.includes('invalid-token')) {
        try {
          // Try to refresh the token
          const user = auth.currentUser;
          if (user && !originalRequest._retry) {
            originalRequest._retry = true;
            
            // Force token refresh
            const newToken = await user.getIdToken(true);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            
            logger.debug('🔄 Token refreshed, retrying request');
            return api(originalRequest);
          }
        } catch (refreshError) {
          logger.error('Token refresh failed:', refreshError);
          
          // Redirect to login or sign out user
          if (auth.currentUser) {
            await auth.signOut();
            // Redirect to the actual sign-in route used by the app
            window.location.href = '/auth/signin';
          }
        }
      }
      
      // Show user-friendly auth error (unless silent endpoint)
      const message = errorData?.message || 'Authentication failed. Please sign in again.';
      if (!isSilentEndpoint) {
        toast.error(message);
      }
    }
    
    // Handle network errors
    else if (!error.response) {
      if (!isSilentEndpoint) {
        const message = error.code === 'ECONNABORTED' 
          ? 'Request timeout. Please check your connection.'
          : 'Network error. Please check your connection.';
        
        toast.error(message);
      }
    }
    
    // Handle server errors
    else if (error.response?.status >= 500) {
      if (!isSilentEndpoint) {
        toast.error('Server error. Please try again later.');
      }
    }
    
    // Handle client errors (4xx)
    else if (error.response?.status >= 400 && error.response?.status < 500) {
      const errorData = error.response.data;
      const message = errorData?.error?.message || errorData?.message || 'Request failed';
      
      // Don't show toast for 404s or silent endpoints (let components handle them)
      if (error.response.status !== 404 && !isSilentEndpoint) {
        toast.error(message);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * API request helpers with error handling
 */
export const apiClient = {
  /**
   * GET request
   * @param {string} url - Request URL
   * @param {Object} config - Axios config
   */
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error) {
      throw processError(error);
    }
  },

  /**
   * POST request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   */
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw processError(error);
    }
  },

  /**
   * PUT request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   */
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw processError(error);
    }
  },

  /**
   * PATCH request
   * @param {string} url - Request URL
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   */
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await api.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw processError(error);
    }
  },

  /**
   * DELETE request
   * @param {string} url - Request URL
   * @param {Object} config - Axios config
   */
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error) {
      throw processError(error);
    }
  }
};

/**
 * Process API errors into a consistent format
 * @param {Error} error - Axios error
 * @returns {Object} Processed error
 */
const processError = (error) => {
  const processedError = {
    message: 'An unexpected error occurred',
    code: 'UNKNOWN_ERROR',
    status: 500,
    original: error
  };

  if (error.response) {
    // Server responded with error status
    const errorData = error.response.data;
    processedError.status = error.response.status;
    processedError.message = errorData?.error?.message || errorData?.message || `HTTP ${error.response.status}`;
    processedError.code = errorData?.error?.code || errorData?.code || 'SERVER_ERROR';
    processedError.details = errorData?.error?.details || errorData?.details;
  } else if (error.request) {
    // Request made but no response received
    processedError.message = 'Network error - please check your connection';
    processedError.code = 'NETWORK_ERROR';
    processedError.status = 0;
  } else {
    // Error in request setup
    processedError.message = error.message || 'Request configuration error';
    processedError.code = 'REQUEST_ERROR';
  }

  return processedError;
};

/**
 * Health check endpoint
 */
export const checkHealth = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/health`, { timeout: 5000 });
    return response.data;
  } catch (error) {
    throw processError(error);
  }
};

/**
 * Items API endpoints
 */
export const itemsAPI = {
  /**
   * Get all items for authenticated user
   * @param {Object} params - Query parameters
   */
  getItems: (params = {}) => apiClient.get('/items', { params }),

  /**
   * Get item history for autocomplete
   * @param {string} search - Search term
   * @param {number} limit - Maximum results
   */
  getHistory: (search = '', limit = 20) => 
    apiClient.get('/items/history', { params: { search, limit } }),

  /**
   * Get user statistics
   */
  getStats: () => apiClient.get('/items/stats'),
  /**
   * Get expense timeline grouped by period with timezone
   * @param {'daily'|'weekly'|'monthly'} groupBy
   * @param {string} tz IANA timezone (e.g., 'Europe/London')
   * @param {object} options optional window sizes
   */
  getExpenseTimeline: (groupBy = 'daily', tz = Intl.DateTimeFormat().resolvedOptions().timeZone, options = {}) =>
    apiClient.get('/expenses/timeline', { params: { groupBy, tz, ...options } }),

  /**
   * Create a new item
   * @param {Object} itemData - Item data
   */
  createItem: (itemData) => apiClient.post('/items', itemData),

  /**
   * Reuse item from history
   * @param {Object} itemData - Item data
   */
  reuseItem: (itemData) => apiClient.post('/items/reuse', itemData),

  /**
   * Update an existing item
   * @param {string} itemId - Item ID
   * @param {Object} updates - Updates to apply
   */
  updateItem: (itemId, updates) => apiClient.patch(`/items/${itemId}`, updates),

  /**
   * Toggle item bought status
   * @param {string} itemId - Item ID
   */
  toggleItem: (itemId) => apiClient.patch(`/items/${itemId}/toggle`),

  /**
   * Delete an item
   * @param {string} itemId - Item ID
   */
  deleteItem: (itemId) => apiClient.delete(`/items/${itemId}`),

  /**
   * Create refund transaction for an item
   * @param {string} itemId - Item ID
   * @param {Object} refundData - Refund data (type, quantity, amount)
   */
  createRefundTransaction: (itemId, refundData) => 
    apiClient.post(`/items/${itemId}/refund`, refundData),

  /**
   * Clear all purchased items (soft delete)
   */
  clearPurchased: (listName = null) => apiClient.delete('/items/purchased', { data: { listName } }),
  
  /**
   * Clear all items to start a new shopping list
   */
  clearAll: (listName = null) => apiClient.delete('/items/all', { data: { listName } }),
  
  /**
   * Restore last cleared purchased batch within undo window
   */
  restoreLastCleared: () => apiClient.post('/items/restore-last'),
  /**
   * Get clear history metadata
   */
  getClearHistory: () => apiClient.get('/items/clear-history'),
  
  /**
   * Get all clear history entries
   */
  getAllClearHistory: (limit = 50) => apiClient.get('/items/clear-history/all', { params: { limit } }),
  
  /**
   * Restore a specific clear history entry
   */
  restoreClearHistoryEntry: (historyId) => apiClient.post(`/items/clear-history/${historyId}/restore`),
  
  /**
   * Delete a clear history entry
   */
  deleteClearHistoryEntry: (historyId) => apiClient.delete(`/items/clear-history/${historyId}`),
  /**
   * Expense summary (today, week, month)
   */
  getExpenseSummary: (tz = Intl.DateTimeFormat().resolvedOptions().timeZone) =>
    apiClient.get('/expenses/summary', { params: { tz } }),

  /**
   * Batch update multiple items
   * @param {Array} items - Array of {id, updates} objects
   */
  batchUpdate: (items) => apiClient.patch('/items/batch', { items })
};

/**
 * User API endpoints
 */
export const userAPI = {
  /**
   * Get current user profile
   */
  getProfile: () => apiClient.get('/user/profile'),

  /**
   * Export the authenticated user's data snapshot
   */
  exportData: () => apiClient.get('/user/export'),

  /**
   * Permanently delete the authenticated user's account
   * @param {{confirmPhrase: string, feedback?: string}} payload
   */
  deleteAccount: (payload) => apiClient.delete('/user', { data: payload })
};

/**
 * Billing API endpoints
 */
export const billingAPI = {
  /**
   * Create a Stripe Checkout session and return URL
   */
  createCheckoutSession: async () => apiClient.post('/billing/create-checkout-session'),

  /**
   * Create a Stripe Billing Portal session and return URL
   */
  createPortalSession: async () => apiClient.post('/billing/manage-portal'),

  /**
   * Get current premium status (fresh from backend claims)
   */
  getStatus: async () => apiClient.get('/billing/status')
};

/**
 * Premium API endpoints
 */
export const premiumAPI = {
  // Categories
  listCategories: () => apiClient.get('/premium/categories'),
  createCategory: (data) => apiClient.post('/premium/categories', data),
  updateCategory: (id, data) => apiClient.put(`/premium/categories/${id}`, data),
  deleteCategory: (id) => apiClient.delete(`/premium/categories/${id}`),

  // Budgets
  listBudgets: (month) => apiClient.get('/premium/budgets', { params: month ? { month } : {} }),
  getBudgetNudges: () => apiClient.get('/premium/budgets/nudges'),
  createBudget: (data) => apiClient.post('/premium/budgets', data),
  updateBudget: (id, data) => apiClient.put(`/premium/budgets/${id}`, data),
  deleteBudget: (id) => apiClient.delete(`/premium/budgets/${id}`),

  // Transactions
  listTransactions: (month) => apiClient.get('/premium/transactions', { params: month ? { month } : {} }),
  createTransaction: (data) => apiClient.post('/premium/transactions', data),
  updateTransaction: (id, data) => apiClient.put(`/premium/transactions/${id}`, data),
  deleteTransaction: (id) => apiClient.delete(`/premium/transactions/${id}`),

  // Stats
  getStats: (month) => apiClient.get('/premium/stats', { params: month ? { month } : {} })
};

// Finance Planner extras (Phase 1 stubs)
premiumAPI.getSummary = (month) => apiClient.get('/premium/analytics/summary', { params: month ? { month } : {} });
premiumAPI.getHealthLatest = () => apiClient.get('/premium/health/latest');
premiumAPI.testRule = (payload) => apiClient.post('/premium/rules/test', payload);
premiumAPI.createMonthlyReport = (month) => apiClient.post('/premium/report/monthly', { month });
premiumAPI.listGoals = () => apiClient.get('/premium/goals');
premiumAPI.listRules = () => apiClient.get('/premium/rules');
premiumAPI.confirmTransactionCategory = (id, categoryId, wasCorrection=false) => apiClient.post(`/premium/transactions/${id}/confirm-category`, { categoryId, wasCorrection });

/**
 * Overview API
 */
export const overviewAPI = {
  getOverview: () => apiClient.get('/overview'),
  getAiSummary: (payload = {}) => apiClient.post('/overview/ai-summary', payload)
};

// Sharing API
const createShare = (data = {}) => apiClient.post('/share', data);

const isNoItemsToShareError = (error) => {
  const validation = error?.original?.response?.data?.error?.validation;
  return validation?.reason === 'NO_ITEMS_TO_SHARE';
};

const createShareForList = async (listId, data = {}) => {
  if (!listId) {
    return Promise.reject(new Error('listId is required to create a share link'));
  }

  try {
    return await apiClient.post(`/share/list/${listId}`, data);
  } catch (error) {
    if (isNoItemsToShareError(error)) {
      const fallbackPayload = {
        ...data,
        includeCompleted: true
      };
      return createShare(fallbackPayload);
    }
    throw error;
  }
};

export const shareAPI = {
  createShare,
  createShareForList,

  // Get all user's shared lists
  getMyShares: () => apiClient.get('/share/my-shares'),
  
  // Delete a shared list
  deleteShare: (shareId) => apiClient.delete(`/share/${shareId}`),
  
  // Get a shared list by ID (public - no auth)
  getSharedList: (shareId) => api.get(`/shared/${shareId}`)
};

// Export the configured axios instance for custom requests
export { api };

/**
 * AI Insights API endpoints
 */
export const insightsAPI = {
  // Get complete dashboard insights with AI
  getDashboardInsights: (startDate, endDate) => 
    apiClient.get('/insights/dashboard', {
      params: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString()
      }
    }),
  
  // Ask a specific question about insights
  askQuestion: (question, startDate, endDate) =>
    apiClient.post('/insights/ask', {
      question,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    }),
  
  // Enhance a description using AI
  enhanceDescription: (description, category, subcategory = null, type = 'item') =>
    apiClient.post('/insights/enhance-description', {
      description,
      category,
      subcategory,
      type
    })
};

export const listsAPI = {
  // Get a single shopping list by ID
  getListById: (listId) =>
    apiClient.get(`/lists/${listId}`),
  
  // Duplicate a shopping list
  duplicateList: (listId) =>
    apiClient.post(`/lists/${listId}/duplicate`),
  
  // Delete a shopping list
  deleteList: (listId) =>
    apiClient.delete(`/lists/${listId}`)
};

// Export API client as default
export default apiClient;
